import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaGlassCheers, FaBirthdayCake, FaRing, FaCalendarAlt, FaMicrophoneAlt, FaSlidersH, FaVolumeUp, FaGraduationCap, FaRocket } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from './SEO';
import './CommercialPage.css';

const CommercialPage = () => {
  const { t } = useLanguage();
  
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
  };

  const services = [
    {
      icon: <FaRing />,
      title: t('commercial.weddings'),
      description: t('commercial.weddingsDesc')
    },
    {
      icon: <FaBirthdayCake />,
      title: t('commercial.birthdays'),
      description: t('commercial.birthdaysDesc')
    },
    {
      icon: <FaGlassCheers />,
      title: t('commercial.corporate'),
      description: t('commercial.corporateDesc')
    },
    {
      icon: <FaCalendarAlt />,
      title: t('commercial.anniversaries'),
      description: t('commercial.anniversariesDesc')
    },
    {
      icon: <FaGraduationCap />,
      title: t('commercial.graduation'),
      description: t('commercial.graduationDesc')
    },
    {
      icon: <FaRocket />,
      title: t('commercial.productLaunches'),
      description: t('commercial.productLaunchesDesc')
    }
  ];

  return (
    <>
      <SEO 
        title="Commercial Projects"
        description="Commercial DJ services by Sergey Levitskiy - Professional DJ for weddings, corporate events, celebrations, and special occasions. High-quality sound and lighting."
        keywords="Commercial DJ, Wedding DJ, Event DJ, Corporate DJ, Celebration DJ, Sergey Levitskiy commercial"
      />
      <div className="commercial-page">
      {/* Креативные эффекты фона для Commercial */}
      <div className="commercial-confetti"></div>
      <div className="commercial-glow"></div>
      
      <motion.div
        className="commercial-content"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
      >
        {/* Hero Section */}
        <motion.section
          className="commercial-hero"
          variants={sectionVariants}
        >
          <div className="hero-decoration"></div>
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="hero-icon"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
            >
            </motion.div>
            <h1 className="commercial-title">{t('commercial.title')}</h1>
            <p className="commercial-subtitle">{t('commercial.subtitle')}</p>
            <div className="hero-divider"></div>
          </motion.div>
        </motion.section>

        {/* Main Description */}
        <motion.section
          className="description-section"
          variants={sectionVariants}
        >
          <div className="description-content">
            <h2 className="section-title">{t('commercial.mainTitle')}</h2>
            <div className="description-text">
              <p>
                {t('commercial.description1')}
              </p>
              <p>
                {t('commercial.description2')}
              </p>
              <p>
                {t('commercial.description3')}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Services Grid */}
        <motion.section
          className="services-section"
          variants={sectionVariants}
        >
          <h2 className="section-title">{t('commercial.services')}</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Features Section */}
        <motion.section
          className="features-section"
          variants={sectionVariants}
        >
          <h2 className="section-title">{t('commercial.whatYouGet')}</h2>
          <div className="features-list">
            <motion.div className="feature-item" variants={itemVariants}>
              <FaMicrophoneAlt className="feature-icon" />
              <div className="feature-content">
                <h3>{t('commercial.equipment')}</h3>
                <p>{t('commercial.equipmentDesc')}</p>
              </div>
            </motion.div>
            <motion.div className="feature-item" variants={itemVariants}>
              <FaSlidersH className="feature-icon" />
              <div className="feature-content">
                <h3>{t('commercial.individual')}</h3>
                <p>{t('commercial.individualDesc')}</p>
              </div>
            </motion.div>
            <motion.div className="feature-item" variants={itemVariants}>
              <FaVolumeUp className="feature-icon" />
              <div className="feature-content">
                <h3>{t('commercial.repertoire')}</h3>
                <p>{t('commercial.repertoireDesc')}</p>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="cta-section"
          variants={sectionVariants}
        >
          <div className="cta-content">
            <h2>{t('commercial.cta')}</h2>
            <p>{t('commercial.ctaDesc')}</p>
            <Link to="/contacts" className="cta-button">
              {t('commercial.contact')}
            </Link>
          </div>
        </motion.section>
      </motion.div>
    </div>
    </>
  );
};

export default CommercialPage;

